import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import LoginPromptModal from '../../components/LoginPromptModal';
import { useAuth } from '../../contexts/AuthContext';
import { playCorrect, playWrong, playClap } from '../../utils/sounds';

// ─── Theme ────────────────────────────────────────────────────────────────────
const PRIMARY    = '#2DBCAF';
const PRIMARY_GR = 'linear-gradient(135deg, #2BBCAF 0%, #1fa99c 50%, #18938a 100%)';
const BG_GR      = 'linear-gradient(160deg, #f9e8d9 0%, #faf0e8 40%, #e8f5f3 100%)';
const FONT       = "'Plus Jakarta Sans', sans-serif";

// ─── Animations ───────────────────────────────────────────────────────────────
const bounce = keyframes`
  0%, 100% { transform: scale(1); }
  40%       { transform: scale(1.15) translateY(-6px); }
  70%       { transform: scale(1.07) translateY(-3px); }
`;
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeInScale = keyframes`
  from { opacity: 0; transform: scale(0.75); }
  to   { opacity: 1; transform: scale(1); }
`;
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-5deg); }
  50%       { transform: translateY(-12px) rotate(5deg); }
`;
const floatDelay = keyframes`
  0%, 100% { transform: translateY(0px) rotate(8deg); }
  50%       { transform: translateY(-8px) rotate(-3deg); }
`;

// ─── Animal Image Helper ──────────────────────────────────────────────────────
const IMG_VERSION = 'v2';
const EMOJI_TO_SLUG: Record<string, string> = {
  '🐘':'elephant','🦁':'lion','🐱':'cat','🐶':'dog','🐔':'chicken',
  '🐄':'cow','🐐':'goat','🦆':'duck','🐟':'fish','🐬':'dolphin',
  '🐋':'whale','🐢':'turtle','🐵':'monkey','🐯':'tiger','🐻':'bear',
  '🐼':'panda','🐨':'koala','🦒':'giraffe','🦓':'zebra','🦏':'rhino',
  '🐴':'horse','🐷':'pig','🐦':'bird','🦅':'eagle','🐧':'penguin',
  '🐍':'snake','🐊':'crocodile','🐸':'frog','🐰':'rabbit','🐭':'mouse',
  '🐹':'hamster','🦊':'fox','🦩':'flamingo','🐝':'bee','🐫':'camel','🦌':'deer',
};

// ─── Intro ────────────────────────────────────────────────────────────────────
const IntroOverlay = styled.div`
  position: fixed; inset: 0; z-index: 900;
  background: ${BG_GR}; display: flex; flex-direction: column;
  align-items: center;
  overflow: hidden; font-family: ${FONT};
`;
const IntroWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  width: 100%;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  position: relative;
`;
const TopNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.88); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.1);
  padding: 10px 16px; display: flex; align-items: center;
`;
const BackBtn = styled.button`
  width: 36px; height: 36px; background: transparent; border: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: #333; transition: color 0.2s;
  &:hover { color: ${PRIMARY}; }
  .material-symbols-outlined { font-size: 26px; }
`;
const CharArea = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center;
  position: relative; min-height: 0;
`;
const FloatAnimal = styled.div<{ $top?: string; $left?: string; $right?: string; $bottom?: string; $delay?: number }>`
  position: absolute;
  top: ${p => p.$top ?? 'auto'}; left: ${p => p.$left ?? 'auto'};
  right: ${p => p.$right ?? 'auto'}; bottom: ${p => p.$bottom ?? 'auto'};
  width: 52px; height: 52px; border-radius: 14px;
  background: rgba(255,255,255,0.88); backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 14px rgba(0,0,0,0.12);
  font-size: 28px;
  animation: ${p => (p.$delay ?? 0) % 2 === 0 ? float : floatDelay} 3.5s ease-in-out ${p => p.$delay ?? 0}s infinite;
`;
const CharCard = styled.div`
  font-size: clamp(72px, 20vw, 110px);
  animation: ${bounce} 2s ease-in-out infinite;
  filter: drop-shadow(0 12px 28px rgba(0,0,0,0.15));
`;
const InfoCard = styled.div`
  background: #fff; border-radius: 32px 32px 0 0;
  padding: 24px 24px max(32px, env(safe-area-inset-bottom));
  box-shadow: 0 -8px 32px rgba(0,0,0,0.08);
  animation: ${fadeInUp} 0.6s ease; font-family: ${FONT};
`;
const Handle = styled.div`width: 40px; height: 4px; background: #e0e0e0; border-radius: 99px; margin: 0 auto 18px;`;
const IntroTitle = styled.h1`
  font-size: clamp(26px, 7vw, 32px); font-weight: 900; margin: 0 0 8px; color: #1a2a2a;
  span { color: ${PRIMARY}; }
`;
const IntroDesc = styled.p`font-size: 14px; color: #7a8a8a; line-height: 1.6; margin: 0 0 16px;`;

// Language selector
const LangRow = styled.div`display: flex; gap: 10px; margin-bottom: 16px;`;
const LangBtn = styled.button<{ $active: boolean }>`
  flex: 1; padding: 10px; border-radius: 14px;
  border: 2px solid ${p => p.$active ? PRIMARY : '#e0e0e0'};
  background: ${p => p.$active ? 'rgba(45,188,175,0.08)' : '#fff'};
  font-size: 22px; cursor: pointer; transition: all 0.2s;
  &:hover { border-color: ${PRIMARY}; }
`;
const StartBtn = styled.button`
  width: 100%; padding: 16px 24px; border-radius: 99px; border: none;
  background: ${PRIMARY_GR}; color: #fff; font-size: 17px; font-weight: 800;
  cursor: pointer; box-shadow: 0 6px 24px rgba(43,188,175,0.4); font-family: ${FONT};
  transition: all 0.2s;
  &:hover { transform: translateY(-2px); }
  &:active { transform: translateY(0); }
`;

// ─── Game ─────────────────────────────────────────────────────────────────────
const GameWrap = styled.div`
  min-height: 100dvh; display: flex; flex-direction: column;
  background: ${BG_GR}; font-family: ${FONT};
`;
const GameNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.92); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.12);
  padding: 10px 16px; display: flex; align-items: center; justify-content: space-between;
`;
const NavLeft = styled.div`display: flex; align-items: center; gap: 8px;`;
const GameTitle = styled.span`font-size: 17px; font-weight: 800; color: #1a2a2a;`;
const ScoreBadge = styled.div`
  background: rgba(45,188,175,0.12); color: ${PRIMARY};
  padding: 6px 14px; border-radius: 10px; font-size: 14px; font-weight: 800;
`;
const GameBody = styled.div`
  flex: 1; display: flex; flex-direction: column;
  align-items: center; padding: 20px 16px; gap: 16px;
  max-width: 520px; margin: 0 auto; width: 100%;
`;
const NameCard = styled.div`
  background: #fff; border-radius: 24px; padding: 20px 28px;
  text-align: center; box-shadow: 0 4px 24px rgba(0,0,0,0.08); width: 100%;
`;
const AnimalName = styled.div`
  font-size: clamp(28px, 8vw, 40px); font-weight: 900; color: #f59e0b;
  text-transform: uppercase; letter-spacing: 3px;
`;
const RoundInfo = styled.div`font-size: 13px; color: #7a8a8a; margin-top: 6px;`;
const OptionsGrid = styled.div`
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; width: 100%;
`;
const AnimalCard = styled.button<{ $state: 'default' | 'correct' | 'incorrect' }>`
  padding: 16px; border-radius: 20px; border: 3px solid;
  border-color: ${p => p.$state === 'correct' ? '#22c55e' : p.$state === 'incorrect' ? '#ef4444' : '#e2e8f0'};
  background: ${p => p.$state === 'correct' ? 'rgba(34,197,94,0.08)' : p.$state === 'incorrect' ? 'rgba(239,68,68,0.08)' : '#fff'};
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  min-height: 120px; transition: all 0.2s;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  &:hover:not(:disabled) { border-color: ${PRIMARY}; transform: translateY(-2px); box-shadow: 0 6px 20px rgba(45,188,175,0.2); }
  &:disabled { cursor: default; }
`;
const FeedbackMsg = styled.div<{ $type: 'correct' | 'incorrect' }>`
  background: ${p => p.$type === 'correct' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)'};
  color: ${p => p.$type === 'correct' ? '#16a34a' : '#dc2626'};
  border-radius: 12px; padding: 10px 20px; font-size: 15px; font-weight: 700;
  animation: ${fadeInScale} 0.3s ease;
`;
const ModalOverlay = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
`;
const Modal = styled.div`
  background: #fff; border-radius: 28px; padding: 28px 24px 24px;
  text-align: center; max-width: 300px; width: 88%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  animation: ${fadeInScale} 0.4s cubic-bezier(0.175,0.885,0.32,1.275);
  font-family: ${FONT};
`;
const ModalEmoji = styled.div`font-size: 52px; margin-bottom: 10px;`;
const ModalTitle = styled.div`font-size: 24px; font-weight: 900; color: #1a2a2a; margin-bottom: 6px;`;
const ModalDesc = styled.div`font-size: 14px; color: #7a8a8a; margin-bottom: 20px; line-height: 1.5;`;
const ModalBtnRow = styled.div`display: flex; gap: 8px; justify-content: center;`;
const ModalBtn = styled.button<{ $primary?: boolean }>`
  padding: 11px 22px; border-radius: 99px;
  border: ${p => p.$primary ? 'none' : '2px solid #e0e0e0'};
  background: ${p => p.$primary ? PRIMARY_GR : '#fff'};
  color: ${p => p.$primary ? '#fff' : '#555'};
  font-size: 14px; font-weight: 700; cursor: pointer; font-family: ${FONT};
  box-shadow: ${p => p.$primary ? '0 4px 16px rgba(43,188,175,0.4)' : 'none'};
  transition: all 0.2s;
  &:hover { transform: translateY(-1px); }
`;

// ─── Data ─────────────────────────────────────────────────────────────────────
type Language = 'id' | 'en';
interface Animal { nameId: string; nameEn: string; emoji: string; }

const ANIMALS: Animal[] = [
  { nameId:'Gajah',    nameEn:'Elephant',  emoji:'🐘' },
  { nameId:'Singa',    nameEn:'Lion',      emoji:'🦁' },
  { nameId:'Kucing',   nameEn:'Cat',       emoji:'🐱' },
  { nameId:'Anjing',   nameEn:'Dog',       emoji:'🐶' },
  { nameId:'Ayam',     nameEn:'Chicken',   emoji:'🐔' },
  { nameId:'Sapi',     nameEn:'Cow',       emoji:'🐄' },
  { nameId:'Kambing',  nameEn:'Goat',      emoji:'🐐' },
  { nameId:'Bebek',    nameEn:'Duck',      emoji:'🦆' },
  { nameId:'Ikan',     nameEn:'Fish',      emoji:'🐟' },
  { nameId:'Lumba-lumba', nameEn:'Dolphin',emoji:'🐬' },
  { nameId:'Paus',     nameEn:'Whale',     emoji:'🐋' },
  { nameId:'Kura-kura',nameEn:'Turtle',    emoji:'🐢' },
  { nameId:'Monyet',   nameEn:'Monkey',    emoji:'🐵' },
  { nameId:'Harimau',  nameEn:'Tiger',     emoji:'🐯' },
  { nameId:'Beruang',  nameEn:'Bear',      emoji:'🐻' },
  { nameId:'Panda',    nameEn:'Panda',     emoji:'🐼' },
  { nameId:'Koala',    nameEn:'Koala',     emoji:'🐨' },
  { nameId:'Jerapah',  nameEn:'Giraffe',   emoji:'🦒' },
  { nameId:'Zebra',    nameEn:'Zebra',     emoji:'🦓' },
  { nameId:'Badak',    nameEn:'Rhino',     emoji:'🦏' },
  { nameId:'Kuda',     nameEn:'Horse',     emoji:'🐴' },
  { nameId:'Babi',     nameEn:'Pig',       emoji:'🐷' },
  { nameId:'Elang',    nameEn:'Eagle',     emoji:'🦅' },
  { nameId:'Pinguin',  nameEn:'Penguin',   emoji:'🐧' },
  { nameId:'Ular',     nameEn:'Snake',     emoji:'🐍' },
  { nameId:'Buaya',    nameEn:'Crocodile', emoji:'🐊' },
  { nameId:'Katak',    nameEn:'Frog',      emoji:'🐸' },
  { nameId:'Kelinci',  nameEn:'Rabbit',    emoji:'🐰' },
  { nameId:'Tikus',    nameEn:'Mouse',     emoji:'🐭' },
];
const MAX_ROUNDS = 10;

function speak(text: string, lang: Language) {
  if (!('speechSynthesis' in window)) return;
  speechSynthesis.cancel();

  const isId = lang === 'id';
  const voices = speechSynthesis.getVoices();
  const pickVoice = (l: string) => voices.find(v => v.lang === l)
    || voices.find(v => v.lang.startsWith(l.split('-')[0]))
    || null;

  // First: expressive intro ("Ini adalah..." / "This is...")
  const intro = new SpeechSynthesisUtterance(isId ? `Ini adalah... ${text}!` : `This is... ${text}!`);
  intro.lang  = isId ? 'id-ID' : 'en-US';
  intro.rate  = isId ? 0.78 : 0.82;
  intro.pitch = 1.25;
  const v1 = pickVoice(intro.lang);
  if (v1) intro.voice = v1;

  // Second: repeat name slowly and clearly
  const repeat = new SpeechSynthesisUtterance(text);
  repeat.lang  = intro.lang;
  repeat.rate  = 0.65;
  repeat.pitch = 1.1;
  if (v1) repeat.voice = v1;

  speechSynthesis.speak(intro);
  speechSynthesis.speak(repeat);
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AnimalGame() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [showIntro, setShowIntro]   = useState(true);
  const language: Language = 'id';
  const [questions, setQuestions]   = useState<Animal[]>([]);
  const [current, setCurrent]       = useState<Animal | null>(null);
  const [options, setOptions]       = useState<string[]>([]);
  const [score, setScore]           = useState(0);
  const [round, setRound]           = useState(1);
  const [started, setStarted]       = useState(false);
  const [btnState, setBtnState]     = useState<Record<string, 'default' | 'correct' | 'incorrect'>>({});
  const [feedback, setFeedback]     = useState<{ type: 'correct' | 'incorrect'; msg: string } | null>(null);
  const [gameOver, setGameOver]     = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const loginShownRef = useRef(false);

  const getName = (a: Animal) => language === 'id' ? a.nameId : a.nameEn;

  const generateRound = useCallback((q: Animal[], r: number, lang: Language) => {
    const item = q[r - 1];
    const wrong = ANIMALS.filter(a => a.emoji !== item.emoji).sort(() => Math.random() - 0.5).slice(0, 3);
    setOptions([item, ...wrong].sort(() => Math.random() - 0.5).map(a => a.emoji));
    setCurrent(item);
    setBtnState({});
    setFeedback(null);
    const name = lang === 'id' ? item.nameId : item.nameEn;
    setTimeout(() => speak(name, lang), 400);
    // Show login prompt at round 2 if not logged in
    if (r >= 2 && !loginShownRef.current) {
      setTimeout(() => {
        if (!loginShownRef.current) {
          loginShownRef.current = true;
          setShowLoginPrompt(true);
        }
      }, 500);
    }
  }, []);

  const startGame = useCallback(() => {
    const q = [...ANIMALS].sort(() => Math.random() - 0.5).slice(0, MAX_ROUNDS);
    loginShownRef.current = !!user;
    setQuestions(q); setScore(0); setRound(1); setGameOver(false);
    setStarted(true);
    generateRound(q, 1, language);
  }, [language, generateRound, user]);

  const handleClick = (emoji: string) => {
    if (Object.keys(btnState).length > 0) return; // already answered
    if (emoji === current!.emoji) {
      setBtnState({ [emoji]: 'correct' });
      setFeedback({ type: 'correct', msg: '✅ Benar! +10' });
      setScore(s => s + 10);
      playCorrect();
      setTimeout(() => {
        if (round >= MAX_ROUNDS) {
          setGameOver(true);
          playClap();
        } else {
          const nr = round + 1; setRound(nr); generateRound(questions, nr, language);
        }
      }, 1200);
    } else {
      // Show wrong answer + correct answer highlighted, then advance
      setBtnState({ [emoji]: 'incorrect', [current!.emoji]: 'correct' });
      setFeedback({ type: 'incorrect', msg: `❌ Salah! Yang benar: ${language === 'id' ? current!.nameId : current!.nameEn}` });
      playWrong();
      setTimeout(() => {
        if (round >= MAX_ROUNDS) {
          setGameOver(true);
        } else {
          const nr = round + 1; setRound(nr); generateRound(questions, nr, language);
        }
      }, 1600);
    }
  };

  if (showIntro) {
    return (
      <IntroOverlay>
        <IntroWrapper>
        <TopNav><BackBtn onClick={() => navigate('/')}><span className="material-symbols-outlined">arrow_back</span></BackBtn></TopNav>
        <CharArea>
          <FloatAnimal $top="16%" $left="8%" $delay={0}>
            <img src={`/assets/images/animals/animal_lion.png?${IMG_VERSION}`} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />
          </FloatAnimal>
          <FloatAnimal $top="12%" $right="10%" $delay={1}>
            <img src={`/assets/images/animals/animal_elephant.png?${IMG_VERSION}`} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />
          </FloatAnimal>
          <FloatAnimal $bottom="22%" $left="10%" $delay={2}>
            <img src={`/assets/images/animals/animal_giraffe.png?${IMG_VERSION}`} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />
          </FloatAnimal>
          <FloatAnimal $bottom="18%" $right="8%" $delay={3}>
            <img src={`/assets/images/animals/animal_penguin.png?${IMG_VERSION}`} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />
          </FloatAnimal>
          <CharCard>
            <img src={`/assets/images/animals/animal_koala.png?${IMG_VERSION}`} alt="koala" style={{ width: 'clamp(90px, 22vw, 130px)', height: 'clamp(90px, 22vw, 130px)', objectFit: 'contain' }} />
          </CharCard>
        </CharArea>
        <InfoCard>
          <Handle />
          <IntroTitle>Tebak <span>Hewan!</span></IntroTitle>
          <IntroDesc>Dengarkan nama hewan lalu pilih gambar yang tepat. Yuk belajar mengenal hewan! 🦁</IntroDesc>
          <StartBtn onClick={() => { setShowIntro(false); startGame(); }}>Mulai Bermain</StartBtn>
        </InfoCard>
        </IntroWrapper>
      </IntroOverlay>
    );
  }

  return (
    <GameWrap>
      <GameNav>
        <NavLeft>
          <BackBtn onClick={() => setShowIntro(true)}><span className="material-symbols-outlined">arrow_back</span></BackBtn>
          <GameTitle>Tebak Hewan</GameTitle>
        </NavLeft>
        <ScoreBadge>Skor: {score}</ScoreBadge>
      </GameNav>
      <GameBody>
        <NameCard>
          <AnimalName>{current && getName(current)}</AnimalName>
          <RoundInfo>Ronde {round} / {MAX_ROUNDS}</RoundInfo>
        </NameCard>

        {feedback && <FeedbackMsg $type={feedback.type}>{feedback.msg}</FeedbackMsg>}

        <OptionsGrid>
          {options.map(emoji => {
            const slug = EMOJI_TO_SLUG[emoji];
            return (
              <AnimalCard
                key={emoji}
                $state={btnState[emoji] ?? 'default'}
                disabled={btnState[current?.emoji ?? ''] === 'correct'}
                onClick={() => handleClick(emoji)}
              >
                {slug
                  ? <img src={`/assets/images/animals/animal_${slug}.png?${IMG_VERSION}`} alt={emoji} style={{ width: 90, height: 90, objectFit: 'contain' }} />
                  : <span style={{ fontSize: 72 }}>{emoji}</span>}
              </AnimalCard>
            );
          })}
        </OptionsGrid>
      </GameBody>

      {showLoginPrompt && !user && !authLoading && (
        <LoginPromptModal
          
          onClose={() => setShowLoginPrompt(false)}
          subtitle="Silahkan login terlebih dahulu untuk melanjutkan permainan."
          required
        />
      )}

      {gameOver && (
        <ModalOverlay>
          <Modal>
            <ModalEmoji>🏆</ModalEmoji>
            <ModalTitle>Selesai!</ModalTitle>
            <ModalDesc>Skor akhir: <strong>{score}</strong> dari {MAX_ROUNDS * 10}</ModalDesc>
            <ModalBtnRow>
              <ModalBtn onClick={() => navigate("/games")}>Menu</ModalBtn>
              <ModalBtn $primary onClick={startGame}>Main Lagi</ModalBtn>
            </ModalBtnRow>
          </Modal>
        </ModalOverlay>
      )}
    </GameWrap>
  );
}
